import type { Schema } from "../../../../../../redux/constants/OrdersPage/order";

/** One change of the order form that a «No» in the balance dialog can undo. */
export type BalanceChange =
	| { kind: "points"; previous: Schema["points"] }
	| { kind: "code" }
	| { kind: "other"; previous: Partial<Schema> };

/**
 * What one order tab remembers for the balance dialog. It lives while the
 * order window is open: F5 closes the window, so nothing is stored on the server.
 */
export interface BalanceHistory {
	/** The form after the last passed check, with the price that came back. */
	successForm: Schema | null;
	/** The changes since that check, the last one at the end. */
	changes: BalanceChange[];
	/** The index of the last added address. A replaced address does not move it. */
	lastAddressIndex: number | null;
	/**
	 * A row with a street and no house yet: the street and the house are one
	 * action, so «No» returns the route from before the street.
	 */
	streetEdit: {
		index: number;
		before: Schema["points"];
		change: BalanceChange;
	} | null;
	/** The price and the check a saved order was opened with. */
	baseline: { price?: number; checkCardId?: number } | null;
	seen: {
		pointsKey: string;
		otherKey: string;
		otherValues: Partial<Schema>;
		checkCardId?: number;
		points: Schema["points"];
	} | null;
}

/** The form fields, other than the route and the counterparty, that change the price. */
const COST_FIELDS = [
	"rateId",
	"taxiServiceId",
	"scheduledTime",
	"passengersCount",
	"services",
	"taxiClass",
	"additionalCost",
	"waitingTime",
	"idleTime",
	"suburbanDistanceOverride",
	"suburbanDistanceOverrideState",
	"enableHourlyService",
	"includePickupMargin",
	"rateIncludePickupCost",
	"discount",
	"paymentType",
] as const;

const hasCoordinates = (point: any): boolean =>
	Boolean(point?.coordinates?.lat && point?.coordinates?.lng);

/** A street is chosen and the house is still to come. Objects have a name instead. */
const isStreetOnly = (point: any): boolean =>
	Boolean(point?.street) &&
	!point?.house &&
	!point?.customHouse &&
	!point?.name;

/** Typing in a house number is not a route change: only the coordinates count. */
export const getPointsKey = (points: Schema["points"] = []): string =>
	JSON.stringify(
		points.map((point: any) =>
			hasCoordinates(point) ? point.coordinates : null,
		),
	);

const getOtherValues = (form: Schema): Partial<Schema> => {
	const values = COST_FIELDS.reduce(
		(acc, field) => ({ ...acc, [field]: (form as any)[field] }),
		{} as Partial<Schema>,
	);
	// The calculation sets it itself while the round trip is automatic.
	if (!form.autoCalculateRoundTrip) {
		values.isIntercityRoadTrip = form.isIntercityRoadTrip;
	}
	return values;
};

/** No value, `false`, `0` and an empty list mean the same here: nothing is chosen. */
const getOtherKey = (values: Partial<Schema>): string =>
	JSON.stringify(values, (_, value) =>
		!value || (Array.isArray(value) && !value.length) ? null : value,
	);

export const createBalanceHistory = (): BalanceHistory => ({
	successForm: null,
	changes: [],
	lastAddressIndex: null,
	streetEdit: null,
	baseline: null,
	seen: null,
});

const getSeen = (form: Schema): BalanceHistory["seen"] => ({
	pointsKey: getPointsKey(form.points),
	otherKey: getOtherKey(getOtherValues(form)),
	otherValues: getOtherValues(form),
	checkCardId: form.counterparty?.checkCardId,
	points: form.points,
});

const firstChangedIndex = (
	previous: Schema["points"] = [],
	current: Schema["points"] = [],
): number => {
	const index = current.findIndex(
		(point: any, i) =>
			JSON.stringify(point?.coordinates) !==
			JSON.stringify((previous[i] as any)?.coordinates),
	);
	return index === -1 ? current.length - 1 : index;
};

/**
 * Compares the form with the one seen last and writes down what changed. The
 * route is compared step by step, so an inserted address does not look like a
 * change of all the addresses below it.
 */
export const trackBalanceChanges = (
	history: BalanceHistory,
	form: Schema,
): void => {
	const seen = getSeen(form);
	const previous = history.seen;
	history.seen = seen;

	if (!previous) {
		if (form.status && form.status !== "creating") {
			history.successForm = form;
			history.baseline = {
				price: form.price,
				checkCardId: form.counterparty?.checkCardId,
			};
		}
		return;
	}

	if (previous.pointsKey !== seen?.pointsKey) {
		const length = form.points?.length ?? 0;
		const previousLength = previous.points?.length ?? 0;
		const index = firstChangedIndex(previous.points, form.points);
		const { streetEdit } = history;
		const sameEdit =
			streetEdit?.index === index &&
			length === previousLength &&
			isStreetOnly(previous.points?.[index]);
		const before = sameEdit ? streetEdit?.before : previous.points;
		// The house finishes the action the street began: it is not a second one.
		if (sameEdit) {
			history.changes = history.changes.filter(
				(change) => change !== streetEdit?.change,
			);
		}
		const change: BalanceChange = { kind: "points", previous: before };
		history.changes.push(change);
		history.streetEdit =
			length >= previousLength && isStreetOnly(form.points?.[index])
				? { index, before, change }
				: null;
		if (length > previousLength) {
			history.lastAddressIndex = firstChangedIndex(
				previous.points,
				form.points,
			);
		} else if (length < previousLength) {
			history.lastAddressIndex = null;
		}
	}

	if (previous.checkCardId !== seen?.checkCardId) {
		// The code also changes the payment type: this is one action.
		if (seen?.checkCardId) history.changes.push({ kind: "code" });
	} else if (
		// The discount block fills an empty discount itself, the dispatcher did nothing.
		getOtherKey({ ...previous.otherValues, discount: undefined }) !==
			getOtherKey({ ...seen?.otherValues, discount: undefined }) ||
		(previous.otherValues.discount && previous.otherKey !== seen?.otherKey)
	) {
		history.changes.push({
			kind: "other",
			previous: previous.otherValues,
		});
	}
};

/** The check passed: this form is what «No» returns to next time. */
export const rememberBalanceSuccess = (
	history: BalanceHistory,
	form: Schema,
): void => {
	history.successForm = form;
	history.changes = [];
};

/** The form changed by the answer itself must not look like a new change. */
/**
 * The server replaced the form of a saved order: this is what is saved now,
 * not an action of the dispatcher. The first look at a form is `trackBalanceChanges`.
 */
export const syncBalanceWithServer = (
	history: BalanceHistory,
	form: Schema,
): void => {
	if (!history.seen) return;

	history.seen = getSeen(form);
	history.successForm = form;
	history.changes = [];
	history.streetEdit = null;
	history.baseline = {
		price: form.price,
		checkCardId: form.counterparty?.checkCardId,
	};
};

export const skipBalanceTracking = (
	history: BalanceHistory,
	form: Schema,
): void => {
	history.seen = getSeen(form);
};

/** A draft only. Quick distribution may have written an order still marked `creating`. */
export const isNewBalanceOrder = (form: Schema): boolean =>
	(!form.status || form.status === "creating") && !form.orderNumber;

const withoutRoute = {
	price: undefined,
	distance: 0,
} as Partial<Schema>;

const removeLastAddress = (
	form: Schema,
	history: BalanceHistory,
): Partial<Schema> => {
	const points = form.points ?? [];
	if (!points.length) return {};

	const { lastAddressIndex } = history;
	const index =
		lastAddressIndex !== null && lastAddressIndex < points.length
			? lastAddressIndex
			: points.length - 1;
	history.lastAddressIndex = null;

	return { points: points.filter((_, i) => i !== index) };
};

/**
 * «No»: cancels the last action. An address comes off, other changes return
 * to the last passed check, the counterparty stays.
 */
export const getNoAnswerForm = (
	form: Schema,
	history: BalanceHistory,
): Partial<Schema> => {
	// A record that would change nothing is no action: «No» must not be idle.
	const currentKey = getOtherKey(getOtherValues(form));
	let change = history.changes.pop();
	while (
		change?.kind === "other" &&
		getOtherKey(
			getOtherValues({ ...form, ...change.previous } as Schema),
		) === currentKey
	) {
		change = history.changes.pop();
	}
	let update: Partial<Schema> = {};

	if (change?.kind === "points") {
		history.streetEdit = null;
		update = { points: change.previous };
	} else if (change?.kind === "other") {
		update = change.previous;
	} else if (isNewBalanceOrder(form)) {
		// The code came last or there is nothing written down: the code stays.
		update = removeLastAddress(form, history);
	} else if (change?.kind === "code" && history.successForm) {
		// A saved order returns to what it was opened with.
		const { counterparty, code, paymentType } = history.successForm;
		update = { counterparty, code, paymentType };
	}

	const points = update.points ?? form.points;
	return {
		...update,
		...(points?.length ? {} : withoutRoute),
		companyBillMetadata: null,
	} as Partial<Schema>;
};

/**
 * «Yes»: cash. The counterparty and the code come off, the route stays.
 * `clearCounterparty` makes the phone search bring the client's accounts back.
 */
export const getYesAnswerForm = (form: Schema): Partial<Schema> => {
	const isNew = isNewBalanceOrder(form);

	return {
		counterparty: undefined,
		code: "",
		paymentType: "cash",
		clearCounterparty: true,
		// A saved order loses the counterparty only with this mark.
		paymentChangeType: isNew ? undefined : "company_bill_to_cash",
		companyBillMetadata: null,
	} as Partial<Schema>;
};

/**
 * The server replaced the form of an order under the taximeter. The price grows
 * by itself there and «No» has nothing to undo, so the server's warning is not
 * shown. The dialog of the check on saving is an answer to the dispatcher: it
 * stays open while the order is still on the counterparty.
 */
export const getTaximeterBalanceMetadata = (
	serverForm: Schema,
	openForm?: Schema,
): Schema["companyBillMetadata"] => {
	const metadata = serverForm.companyBillMetadata;
	if (!serverForm.processedByTaximeter) return metadata;

	const isFailed = (value: Schema["companyBillMetadata"]) =>
		String(value?.status) === "threshold_failed";

	if (
		isFailed(openForm?.companyBillMetadata) &&
		serverForm.paymentType === "company_bill"
	) {
		return openForm?.companyBillMetadata;
	}
	return isFailed(metadata) ? null : metadata;
};

/** The fast check runs only for a counterparty order under balance control. */
export const shouldCheckBalance = (
	form: Schema,
	baseline: BalanceHistory["baseline"],
): boolean => {
	if (form.paymentType !== "company_bill") return false;
	if (!form.counterparty?.id || !form.counterparty?.checkCardId) return false;
	if (form.companyBillMetadata?.thresholdControl === false) return false;

	// A saved order with the same price and counterparty has passed already.
	return !(
		baseline &&
		baseline.price === form.price &&
		baseline.checkCardId === form.counterparty.checkCardId
	);
};

/**
 * The answer in the dialog does not save a new order by itself: quick
 * distribution waits until the form changes after the answer.
 */
export const getAutoCreateKey = (form: Schema): string =>
	JSON.stringify([
		getPointsKey(form.points),
		form.phone ?? null,
		form.counterparty?.checkCardId ?? null,
		// Without the round trip flag: the calculation may set it after the answer.
		COST_FIELDS.map((field) => (form as any)[field] ?? null),
	]);
